<!-- todo:ai-review -->
<script setup lang="ts">
import { formatTitle } from "@augmented/hierarchy/utils";

const route = useRoute();

// Extract bookId and content path from the catch-all route
// Use immediate values (not computed) so useAsyncData keys are stable across SSR/client
const pathSegments = (() => {
  const path = route.params.path;
  if (Array.isArray(path)) return path;
  if (typeof path === "string") return path.split("/");
  return [];
})();

const bookId = pathSegments[0] || "";
const contentPath = pathSegments.slice(1).join("/");

// Fetch all page data in one consolidated call
const { data, status } = useBookPage(bookId, contentPath);

// Computed properties for template convenience
const bookTitle = computed(
  () => data.value?.bookInfo?.title || formatTitle(bookId),
);

// Only show full-page loading on initial load (no tree data yet)
const isInitialLoading = computed(() => {
  return status.value === "pending" && (!data.value?.tree || data.value.tree.length === 0);
});

// Content is loading but we have the layout already
const isContentLoading = computed(() => {
  return status.value === "pending" && data.value?.tree && data.value.tree.length > 0;
});

const isNotFound = computed(() => {
  return status.value === "success" && (!data.value?.tree || data.value.tree.length === 0);
});

// Set page title
useHead({
  title: computed(() => {
    if (contentPath && data.value?.content?.title) {
      return `${data.value.content.title} - ${bookTitle.value}`;
    }
    return bookTitle.value;
  }),
});
</script>

<template>
  <div v-if="!bookId" class="flex items-center justify-center h-screen">
    <div class="text-center">
      <UIcon
        name="i-heroicons-exclamation-triangle"
        class="w-16 h-16 mx-auto text-amber-500"
      />
      <h1 class="mt-4 text-xl font-semibold">No book specified</h1>
      <NuxtLink
        to="/books"
        class="mt-4 inline-block text-primary-600 hover:underline"
      >
        Return to catalog
      </NuxtLink>
    </div>
  </div>

  <div
    v-else-if="isInitialLoading"
    class="flex items-center justify-center h-screen"
  >
    <UIcon
      name="i-heroicons-arrow-path"
      class="w-8 h-8 animate-spin text-primary-500"
    />
    <span class="ml-2">Loading book...</span>
  </div>

  <div v-else-if="isNotFound" class="flex items-center justify-center h-screen">
    <div class="text-center">
      <UIcon
        name="i-heroicons-book-open"
        class="w-16 h-16 mx-auto text-gray-400"
      />
      <h1 class="mt-4 text-xl font-semibold">Book not found</h1>
      <p class="mt-2 text-gray-600">
        The book "{{ bookId }}" could not be found.
      </p>
      <NuxtLink
        to="/books"
        class="mt-4 inline-block text-primary-600 hover:underline"
      >
        Return to catalog
      </NuxtLink>
    </div>
  </div>

  <BookLayout v-else>
    <template #sidebar>
      <BookTreeNav
        :book-id="bookId"
        :book-title="bookTitle"
        :tree="data?.tree || []"
        :current-path="contentPath"
      />
    </template>

    <template #content>
      <div v-if="isContentLoading" class="flex items-center justify-center h-full">
        <UIcon
          name="i-heroicons-arrow-path"
          class="w-6 h-6 animate-spin text-primary-500"
        />
      </div>
      <BookContentPane
        v-else
        :content="data?.content"
        :book-id="bookId"
        :book-title="bookTitle"
        :current-path="contentPath"
        :current-node="data?.currentNode"
        :book-has-own-content="data?.bookInfo?.hasOwnContent"
        :children="data?.children || []"
      />
    </template>
  </BookLayout>
</template>
